import React, { useState, useEffect } from "react";
import { Wrapper, InputSelect, Option, Label } from "./styled";
import isEmpty from "lodash/isEmpty";
import get from "lodash/get";
import ValidationBar from "../common3/ValidationBar";
import HelpIcon from "../common3/HelpBox/Icon";
import HelpBox from "../common3/HelpBox";
import SkeletonLoader from "../Common/Loaders/SkeletonLoader";
import filterString from "../common3/filterString";
import findFirstOptionValue from "./helpers/findFirstOptionValue";
import useDidMount from "../common3/hooks/useDidMount";

const Dropdown = (props) => {
  const {
    extra = {},
    value = null,
    save = () => {},
    id,
    runEffect,
    effects = {},
    validationError = {},
    disableValidation,
    dispatchEvent,
    htmlTemplates = {},
    minHeight = 55,
    height = 55,
    builder,
  } = props;
  const {
    label = "",
    options = [],
    subLabel = "",
    optionsType = "static",
    staticValue = null,
    effects: extraEffects,
    helpBoxAlwaysVisible = false,
    helpBoxIcon = false,
    helpBoxTemplateId = null,
    helpBoxTemplate = null,
    validation = {},
    autoSelectFirstOption = false,
  } = extra;

  const disabled = props.disabled || extra.disabled;

  const { required = false } = validation;

  const [selectedValue, setSelectedValue] = useState(value);
  const [selectedLabel, setSelectedLabel] = useState(label);
  const [focused, setFocused] = useState(false);
  const [helpBoxOpen, setHelpBoxOpen] = useState(
    helpBoxAlwaysVisible ? true : false
  );
  const [selectToDisplay, setSelectToDisplay] = useState(options);
  const didMount = useDidMount();

  /**
   * MOUNT: Check for default values
   */
  useEffect(() => {
    if (
      value === null &&
      (optionsType === "static" || optionsType === "advanced")
    ) {
      let newValue = null;
      options.forEach((o) => {
        if (o.setDefault) {
          newValue = o.value ? o.value : o.label;
        }
      });

      setSelectedValue(newValue);
    }
  }, []);

  useEffect(() => {
    if (
      (optionsType === "effects" || optionsType === "register") &&
      !isEmpty(extraEffects) &&
      !builder
    ) {
      runEffect({
        source: extraEffects,
        effectId: id,
        componentId: props.sourceFromDatasheetCol ? props.colId : id,
      });
    }
  }, [optionsType, value, runEffect, extraEffects]);

  // Support for dynamic updates
  useEffect(() => {
    if (value === null && extra.defaultStatic === true) {
      const defaultValue = filterString(staticValue, props, {});
      setSelectedValue(defaultValue);
    } else {
      setSelectedValue(value);
    }
  }, [value]);

  useEffect(() => {
    /**
     * selectToDisplay
     *
     * Check if we are effect or register, then we pick from props.effects (if not we pick from options)
     */
    if (
      (optionsType === "effects" || optionsType === "register") &&
      !isEmpty(extraEffects)
    ) {
      const _selectToDisplay = get(effects, [id], null);
      setSelectToDisplay(_selectToDisplay);
      if (autoSelectFirstOption && _selectToDisplay && !value) {
        const firstOption = findFirstOptionValue(_selectToDisplay);
        if (firstOption) {
          setSelectedValue(firstOption);
        }
      }
    }
  }, [effects[id], options]);

  /**
   * SAVE EFFECT
   */
  useEffect(() => {
    if (didMount) {
      if (selectToDisplay) {
        // find the line
        const context = selectToDisplay.find((f) => f.value === selectedValue);

        if (value !== selectedValue) {
          save({ key: id, value: selectedValue, context });
          dispatchEvent("onChange");
        }
      }
    }
    // including save will loop the component
  }, [selectedValue, id, selectToDisplay]);

  /**
   * Validation checks
   *
   * TODO: make re-usable effect
   */
  let error = validationError.critical ? "critical" : null;
  let errorMessage = validationError.critical;
  if (!error) {
    error = validationError.error ? "danger" : null;
    errorMessage = validationError.error;
  }
  if (!error) {
    error = validationError.warning ? "warning" : null;
    errorMessage = validationError.warning;
  }

  useEffect(() => {
    if (!selectedValue) {
      setSelectedLabel(label);
      return;
    }

    if (selectToDisplay) {
      const foundToDisplay = selectToDisplay.find((line) => {
        let { value, label } = line;
        if (typeof line === "string") {
          label = line;
          value = line;
        }
        return value ? value === selectedValue : label === selectedValue;
      });
      setSelectedLabel(
        foundToDisplay && foundToDisplay.label
          ? foundToDisplay.label
          : foundToDisplay
      );
    }
  }, [selectedValue, selectToDisplay, label]);

  /**
   * Ensure that selectToDisplay is array, if selectToDisplay === null then we are waiting for data
   */
  if (!Array.isArray(selectToDisplay)) {
    return <SkeletonLoader />;
  }

  const _helpBoxTemplate =
    helpBoxTemplateId === "_custom"
      ? helpBoxTemplate
      : get(htmlTemplates, [helpBoxTemplateId, "html"], "");

  return (
    <Wrapper className={required ? "sp-required" : ""}>
      {!helpBoxAlwaysVisible && helpBoxIcon && (
        <HelpIcon toggle={() => setHelpBoxOpen(!helpBoxOpen)} />
      )}
      <Label
        selectedValue={selectedValue}
        focus={focused}
        labelSelected={selectedLabel === label}
        error={error}
        disabled={disabled}
      >
        {label}
      </Label>
      <InputSelect
        onChange={(e) => {
          setSelectedValue(e.target.value);
        }}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        disabled={disabled}
        value={value || selectedValue || ""}
        minHeight={minHeight}
        aria-label={label || "dropdown"}
        height={height}
      >
        {!autoSelectFirstOption && <Option value=""></Option>}
        {selectToDisplay &&
          selectToDisplay.map((line, i) => {
            let { label, value } = line;

            if (typeof line === "string") {
              label = line;
              value = line;
            }

            return (
              <Option key={i} value={value ? value : label}>
                {label}
              </Option>
            );
          })}
      </InputSelect>
      {(helpBoxAlwaysVisible || helpBoxIcon) &&
        _helpBoxTemplate &&
        helpBoxOpen && (
          <HelpBox template={_helpBoxTemplate} props={props} value={value} />
        )}
      <ValidationBar
        error={errorMessage}
        subLabel={subLabel}
        disableValidation={disableValidation}
      />
    </Wrapper>
  );
};

export default Dropdown;
